import {
  AreaChart,
  Area,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  PieChart,
  Pie,
  Cell,
  BarChart,
  Bar,
  Legend
} from 'recharts';

import type { PaymentGatewayAuditDashboardStats, PgaOutcomePeriod } from '../../../features/payment-gateway-audit/api/payment-gateway-audit.types';

type WeeklyPoint = { label: string; count: number };
type WeeklyPaymentPoint = { label: string; cash: number; bank: number; total: number };
type DailyPoint = { label: string; events: number };

const CHART_COLORS = {
  cash: 'hsl(142, 71%, 45%)',
  bank: 'hsl(217, 91%, 60%)',
  orgs: 'hsl(262, 83%, 58%)',
  users: 'hsl(31, 95%, 53%)',
  events: 'hsl(200, 70%, 50%)'
};

/** Исходы операций платёжного аудита (stacked bar / donut на tenant-дашборде). */
const OUTCOME_COLORS = {
  ok: 'hsl(142, 71%, 42%)',
  fail: 'hsl(0, 72%, 50%)',
  pending: 'hsl(38, 92%, 50%)'
} as const;

function formatPgaSuccessShare(share: number | null | undefined): string {
  if (share == null || !Number.isFinite(share)) return '—';
  try {
    return new Intl.NumberFormat('ru-RU', { style: 'percent', maximumFractionDigits: 1 }).format(share);
  } catch {
    return '—';
  }
}

/** Stacked bar: объёмы ok / fail / pending по окнам 24ч, 7д, 30д (GET …/payment-gateway-audit/stats). */
export function PaymentGatewayOutcomeStackedBarChart({ stats }: { stats: PaymentGatewayAuditDashboardStats }) {
  const barData = [
    { label: '24 ч', ok: stats.day_24h.ok, fail: stats.day_24h.fail, pending: stats.day_24h.pending },
    { label: '7 дн.', ok: stats.week_7d.ok, fail: stats.week_7d.fail, pending: stats.week_7d.pending },
    { label: '30 дн.', ok: stats.month_30d.ok, fail: stats.month_30d.fail, pending: stats.month_30d.pending }
  ];
  return (
    <ResponsiveContainer width="100%" height={220}>
      <BarChart data={barData} margin={{ top: 8, right: 8, left: 0, bottom: 0 }}>
        <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--ui-border))" />
        <XAxis dataKey="label" tick={{ fontSize: 11 }} stroke="hsl(var(--ui-muted-foreground))" />
        <YAxis tick={{ fontSize: 11 }} stroke="hsl(var(--ui-muted-foreground))" allowDecimals={false} />
        <Tooltip
          contentStyle={{
            background: 'hsl(var(--ui-card))',
            border: '1px solid hsl(var(--ui-border))',
            borderRadius: '4px',
            fontSize: '12px'
          }}
          formatter={(value: unknown) => Number(value ?? 0).toLocaleString('ru-RU')}
        />
        <Legend wrapperStyle={{ fontSize: '12px' }} />
        <Bar dataKey="ok" name="Успешно" stackId="out" fill={OUTCOME_COLORS.ok} radius={[0, 0, 0, 0]} />
        <Bar dataKey="fail" name="Ошибка" stackId="out" fill={OUTCOME_COLORS.fail} />
        <Bar dataKey="pending" name="В обработке" stackId="out" fill={OUTCOME_COLORS.pending} radius={[2, 2, 0, 0]} />
      </BarChart>
    </ResponsiveContainer>
  );
}

/** Donut по одному окну исходов (аудит шлюза). */
export function PaymentGatewayOutcomeDonut({ period, title }: { period: PgaOutcomePeriod; title: string }) {
  const data = [
    { name: 'Успешно', value: period.ok, color: OUTCOME_COLORS.ok },
    { name: 'Ошибка', value: period.fail, color: OUTCOME_COLORS.fail },
    { name: 'В обработке', value: period.pending, color: OUTCOME_COLORS.pending }
  ].filter((d) => d.value > 0);

  if (data.length === 0) {
    return (
      <div className="flex min-h-[188px] flex-col items-center justify-center rounded-md border border-dashed border-[hsl(var(--ui-border))] bg-[hsl(var(--ui-card))] px-2 py-3">
        <p className="staff-m-0 text-xs font-medium uppercase tracking-wide text-[hsl(var(--ui-muted-foreground))]">{title}</p>
        <p className="staff-m-0 mt-1 text-xs text-[hsl(var(--ui-muted-foreground))]">Нет операций</p>
      </div>
    );
  }

  return (
    <div className="min-w-0">
      <p className="staff-m-0 mb-1 text-center text-xs font-medium uppercase tracking-wide text-[hsl(var(--ui-muted-foreground))]">
        {title}
      </p>
      <ResponsiveContainer width="100%" height={180}>
        <PieChart>
          <Pie
            data={data}
            dataKey="value"
            nameKey="name"
            cx="50%"
            cy="50%"
            innerRadius={44}
            outerRadius={68}
            paddingAngle={1}
          >
            {data.map((entry, i) => (
              <Cell key={`${entry.name}-${i}`} fill={entry.color} />
            ))}
          </Pie>
          <Tooltip
            formatter={(value: unknown) => Number(value ?? 0).toLocaleString('ru-RU')}
            contentStyle={{
              background: 'hsl(var(--ui-card))',
              border: '1px solid hsl(var(--ui-border))',
              borderRadius: '4px',
              fontSize: '12px'
            }}
          />
        </PieChart>
      </ResponsiveContainer>
      <p className="staff-m-0 text-center text-xs leading-snug text-[hsl(var(--ui-muted-foreground))]">
        Всего {period.total.toLocaleString('ru-RU')} · доля успеха {formatPgaSuccessShare(period.success_share)}
      </p>
    </div>
  );
}

export function WeeklyPaymentsAreaChart({ data }: { data: WeeklyPaymentPoint[] }) {
  return (
    <ResponsiveContainer width="100%" height={220}>
      <AreaChart data={data} margin={{ top: 8, right: 8, left: 0, bottom: 0 }}>
        <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--ui-border))" />
        <XAxis dataKey="label" tick={{ fontSize: 11 }} stroke="hsl(var(--ui-muted-foreground))" />
        <YAxis tick={{ fontSize: 11 }} stroke="hsl(var(--ui-muted-foreground))" />
        <Tooltip
          contentStyle={{
            background: 'hsl(var(--ui-card))',
            border: '1px solid hsl(var(--ui-border))',
            borderRadius: '4px',
            fontSize: '12px'
          }}
          formatter={(value: unknown) => `${Number(value ?? 0).toLocaleString('ru-RU')} ₽`}
        />
        <Legend wrapperStyle={{ fontSize: '12px' }} />
        <Area type="monotone" dataKey="cash" name="Касса" stackId="1" stroke={CHART_COLORS.cash} fill={CHART_COLORS.cash} fillOpacity={0.4} />
        <Area type="monotone" dataKey="bank" name="Банк" stackId="1" stroke={CHART_COLORS.bank} fill={CHART_COLORS.bank} fillOpacity={0.4} />
      </AreaChart>
    </ResponsiveContainer>
  );
}

export function CashBankPieChart({ cashTotal, bankTotal }: { cashTotal: number; bankTotal: number }) {
  const data = [
    { name: 'Касса', value: cashTotal, color: CHART_COLORS.cash },
    { name: 'Банк', value: bankTotal, color: CHART_COLORS.bank }
  ].filter((d) => d.value > 0);

  if (data.length === 0) {
    return <p className="staff-m-0 text-sm text-[hsl(var(--ui-muted-foreground))]">Нет данных за период</p>;
  }

  return (
    <ResponsiveContainer width="100%" height={200}>
      <PieChart>
        <Pie data={data} dataKey="value" nameKey="name" cx="50%" cy="50%" outerRadius={70} label={({ name, value }) => `${name}: ${Number(value ?? 0).toLocaleString('ru-RU')} ₽`}>
          {data.map((entry, i) => (
            <Cell key={i} fill={entry.color} />
          ))}
        </Pie>
        <Tooltip
          formatter={(value: unknown) => `${Number(value ?? 0).toLocaleString('ru-RU')} ₽`}
          contentStyle={{
            background: 'hsl(var(--ui-card))',
            border: '1px solid hsl(var(--ui-border))',
            borderRadius: '4px',
            fontSize: '12px'
          }}
        />
      </PieChart>
    </ResponsiveContainer>
  );
}

export function WeeklyGrowthAreaChart({ data, label }: { data: WeeklyPoint[]; label: string }) {
  return (
    <ResponsiveContainer width="100%" height={200}>
      <AreaChart data={data} margin={{ top: 8, right: 8, left: 0, bottom: 0 }}>
        <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--ui-border))" />
        <XAxis dataKey="label" tick={{ fontSize: 11 }} stroke="hsl(var(--ui-muted-foreground))" />
        <YAxis tick={{ fontSize: 11 }} stroke="hsl(var(--ui-muted-foreground))" allowDecimals={false} />
        <Tooltip
          contentStyle={{
            background: 'hsl(var(--ui-card))',
            border: '1px solid hsl(var(--ui-border))',
            borderRadius: '4px',
            fontSize: '12px'
          }}
        />
        <Area type="monotone" dataKey="count" name={label} stroke={CHART_COLORS.orgs} fill={CHART_COLORS.orgs} fillOpacity={0.3} />
      </AreaChart>
    </ResponsiveContainer>
  );
}

export function AuditDailyBarChart({ data }: { data: DailyPoint[] }) {
  return (
    <ResponsiveContainer width="100%" height={200}>
      <BarChart data={data} margin={{ top: 8, right: 8, left: 0, bottom: 0 }}>
        <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--ui-border))" />
        <XAxis dataKey="label" tick={{ fontSize: 10 }} stroke="hsl(var(--ui-muted-foreground))" interval={0} angle={-45} textAnchor="end" height={50} />
        <YAxis tick={{ fontSize: 11 }} stroke="hsl(var(--ui-muted-foreground))" allowDecimals={false} />
        <Tooltip
          contentStyle={{
            background: 'hsl(var(--ui-card))',
            border: '1px solid hsl(var(--ui-border))',
            borderRadius: '4px',
            fontSize: '12px'
          }}
        />
        <Bar dataKey="events" name="События" fill={CHART_COLORS.events} radius={[2, 2, 0, 0]} />
      </BarChart>
    </ResponsiveContainer>
  );
}

export { CHART_COLORS, OUTCOME_COLORS };
